import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, ChevronRight } from 'lucide-react';
import { apiClient } from '../api/client';

interface TeamInfo {
  name: string;
  userCount: number;
  onlineCount: number;
}

export default function TeamList() {
  const [teams, setTeams] = useState<TeamInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTeams() {
      setLoading(true);
      try {
        // Get list of team names
        const teamsData = await apiClient.getTeams();

        // For each team, get user count
        const teamInfoPromises = teamsData.teams.map(async (teamName) => {
          try {
            const usersData = await apiClient.getUsers({ team_name: teamName, limit: 1 });
            // Get online users count
            const onlineData = await apiClient.getUsers({ team_name: teamName, status: 'active', limit: 1 });
            return {
              name: teamName,
              userCount: usersData.total,
              onlineCount: onlineData.total,
            };
          } catch {
            return {
              name: teamName,
              userCount: 0,
              onlineCount: 0,
            };
          }
        });

        const teamInfos = await Promise.all(teamInfoPromises);
        setTeams(teamInfos);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load teams');
      } finally {
        setLoading(false);
      }
    }

    fetchTeams();
  }, []);

  const totalUsers = teams.reduce((sum, t) => sum + t.userCount, 0);
  const totalOnline = teams.reduce((sum, t) => sum + t.onlineCount, 0);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
        <div className="flex gap-4">
          <span className="text-sm text-gray-500">{teams.length} teams</span>
          <span className="text-sm text-gray-500">{totalUsers} total users</span>
          <span className="text-sm text-green-600">{totalOnline} online</span>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : teams.length === 0 ? (
        <div className="card text-center py-12">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No teams found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {teams.map((team) => (
            <Link
              key={team.name}
              to={`/users?team=${encodeURIComponent(team.name)}`}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Users size={24} className="text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{team.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{team.userCount} user{team.userCount !== 1 ? 's' : ''}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {team.onlineCount} online
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
